import React, { useState, useEffect, useMemo } from 'react';
import { AuthUser } from 'aws-amplify/auth';
import { Navigate } from 'react-router-dom';
import { getAdminUsers } from '../api';
import { AdminUser } from '../types';
import './Admin.css';

const ADMIN_EMAIL = 'swapnilp1994@gmail.com';

interface Props {
  user: AuthUser | null;
}

function fmt(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
  } catch { return '—'; }
}

function relTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const d = Math.floor(diff / 86400000);
    if (d === 0) return 'Today';
    if (d === 1) return 'Yesterday';
    if (d < 30) return `${d}d ago`;
    const m = Math.floor(d / 30);
    return `${m}mo ago`;
  } catch { return '—'; }
}

export default function Admin({ user }: Props) {
  // All hooks must come before any conditional returns
  const [data, setData] = useState<{ users: AdminUser[]; total: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'points' | 'picks' | 'signUpDate' | 'lastPickAt'>('points');
  const [expandedUser, setExpandedUser] = useState<string | null>(null);

  const isAdmin = user?.signInDetails?.loginId === ADMIN_EMAIL;

  useEffect(() => {
    if (!isAdmin) return;
    getAdminUsers().then(res => {
      if (res) setData(res);
      else setError(true);
    }).finally(() => setLoading(false));
  }, [isAdmin]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase();
    let list = data.users.filter(u =>
      !q ||
      u.displayName.toLowerCase().includes(q) ||
      (u.email ?? '').toLowerCase().includes(q) ||
      (u.countryName ?? '').toLowerCase().includes(q)
    );
    list = [...list].sort((a, b) => {
      if (sortBy === 'points') return b.points - a.points;
      if (sortBy === 'picks') return b.pickCount - a.pickCount;
      if (sortBy === 'signUpDate') return (b.signUpDate ?? '').localeCompare(a.signUpDate ?? '');
      if (sortBy === 'lastPickAt') return (b.lastPickAt ?? '').localeCompare(a.lastPickAt ?? '');
      return 0;
    });
    return list;
  }, [data, search, sortBy]);

  const totalPicks  = data?.users.reduce((s, u) => s + u.pickCount, 0) ?? 0;
  const totalPoints = data?.users.reduce((s, u) => s + u.points, 0) ?? 0;
  const activeUsers = data?.users.filter(u => u.pickCount > 0).length ?? 0;

  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="admin-page container">
      <div className="matches-header">
        <h1 className="page-title">Admin Dashboard</h1>
        <p className="page-sub">User management and activity</p>
      </div>

      {loading && (
        <div className="admin-loading">
          <div className="admin-spinner" />
          <span>Loading users…</span>
        </div>
      )}

      {error && (
        <div className="admin-error">Failed to load user data. Check that you have admin access.</div>
      )}

      {!loading && data && (
        <>
          {/* Summary stats */}
          <div className="admin-stats-bar">
            <div className="admin-stat-chip">
              <span className="asc-val">{data.total}</span>
              <span className="asc-label">Total Users</span>
            </div>
            <div className="admin-stat-chip">
              <span className="asc-val">{activeUsers}</span>
              <span className="asc-label">Active (has picks)</span>
            </div>
            <div className="admin-stat-chip">
              <span className="asc-val">{totalPicks}</span>
              <span className="asc-label">Total Picks</span>
            </div>
            <div className="admin-stat-chip">
              <span className="asc-val">{totalPoints}</span>
              <span className="asc-label">Total Points</span>
            </div>
          </div>

          {/* Controls */}
          <div className="admin-controls">
            <input
              className="admin-search"
              placeholder="Search by name, email, or country…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div className="admin-sort">
              <span className="admin-sort-label">Sort:</span>
              {(['points', 'picks', 'signUpDate', 'lastPickAt'] as const).map(s => (
                <button
                  key={s}
                  className={`admin-sort-btn${sortBy === s ? ' active' : ''}`}
                  onClick={() => setSortBy(s)}
                >
                  {s === 'points' ? 'Points' : s === 'picks' ? 'Picks' : s === 'signUpDate' ? 'Sign Up' : 'Last Active'}
                </button>
              ))}
            </div>
          </div>

          {/* User table */}
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Email</th>
                  <th>Location</th>
                  <th>Supporting</th>
                  <th>Picks</th>
                  <th>Correct</th>
                  <th>Points</th>
                  <th>Joined</th>
                  <th>Last Active</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u, i) => (
                  <React.Fragment key={u.userId}>
                    <tr
                      className={`admin-row${expandedUser === u.userId ? ' admin-row--expanded' : ''}`}
                      onClick={() => setExpandedUser(prev => prev === u.userId ? null : u.userId)}
                    >
                      <td className="admin-rank">{i + 1}</td>
                      <td className="admin-user-cell">
                        <div className="admin-avatar">{u.displayName.slice(0, 2).toUpperCase()}</div>
                        <span className="admin-displayname">{u.displayName}</span>
                      </td>
                      <td className="admin-email">{u.email ?? <span className="admin-null">—</span>}</td>
                      <td className="admin-country">
                        {u.countryFlag
                          ? <span title={u.countryName ?? ''}>{u.countryFlag} {u.countryName}</span>
                          : <span className="admin-null">—</span>}
                      </td>
                      <td className="admin-country">
                        {u.supportingCountryFlag
                          ? <span title={u.supportingCountryName ?? ''}>{u.supportingCountryFlag} {u.supportingCountryName}</span>
                          : <span className="admin-null">—</span>}
                      </td>
                      <td className="admin-num">{u.pickCount}</td>
                      <td className="admin-num">
                        {u.correctCount > 0
                          ? <span className="admin-correct">{u.correctCount}</span>
                          : u.correctCount}
                      </td>
                      <td className="admin-num admin-points">{u.points}</td>
                      <td className="admin-date">{fmt(u.signUpDate)}</td>
                      <td className="admin-date">{relTime(u.lastPickAt)}</td>
                      <td>
                        <span className={`admin-status-badge ${u.enabled === false ? 'disabled' : u.accountStatus === 'CONFIRMED' ? 'confirmed' : 'pending'}`}>
                          {u.enabled === false ? 'Disabled' : u.accountStatus ?? '—'}
                        </span>
                      </td>
                    </tr>
                    {expandedUser === u.userId && (
                      <tr className="admin-expand-row">
                        <td colSpan={11}>
                          <div className="admin-expand-detail">
                            <div className="admin-detail-item">
                              <span className="admin-detail-key">User ID</span>
                              <span className="admin-detail-val admin-mono">{u.userId}</span>
                            </div>
                            <div className="admin-detail-item">
                              <span className="admin-detail-key">Accuracy</span>
                              <span className="admin-detail-val">
                                {u.pickCount > 0 && u.correctCount >= 0
                                  ? `${Math.round((u.correctCount / Math.max(u.pickCount, 1)) * 100)}%`
                                  : '—'}
                              </span>
                            </div>
                            <div className="admin-detail-item">
                              <span className="admin-detail-key">Last Pick</span>
                              <span className="admin-detail-val">{fmt(u.lastPickAt)}</span>
                            </div>
                            <div className="admin-detail-item">
                              <span className="admin-detail-key">Account Enabled</span>
                              <span className="admin-detail-val">{u.enabled === null ? '—' : u.enabled ? 'Yes' : 'No'}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="admin-empty">No users match your search.</div>
            )}
          </div>

          <p className="admin-footer-note">{filtered.length} of {data.total} users shown</p>
        </>
      )}
    </div>
  );
}
